import { UseFormMethods } from 'react-hook-form';
import { getTrunkedNetworkUidsFromNic } from '../../../../../../Configure/Utils';
import { NetworkingFormData } from '../../NetworkingForm';

export const trunkedEnabledFieldOptions = {
  shouldValidate: true,
};

export function getRestoredTrunkedNetworks(
  stashed: string[],
  nic: VmNetworkInterfaceToEdit,
): string[] {
  return stashed.length > 0 ? stashed : getTrunkedNetworkUidsFromNic(nic);
}

export function isSwitchingToTrunk(
  formData: NetworkingFormData,
  nic: VmNetworkInterfaceToEdit,
): boolean {
  return Boolean(formData.trunked) && !nic.trunk?.enabled;
}

export type EditNicFormOriginalValues = {
  assignDhcp?: boolean;
  ipAddress?: string;
  macAddress?: string;
  networkName?: string;
  trunked?: boolean;
  trunkedNetworks?: string[];
  type: string;
};

export function getEditNicFormOriginalValues(
  nic: VmNetworkInterfaceToEdit,
): EditNicFormOriginalValues {
  const trunked = Boolean(nic.trunk?.enabled);

  return {
    assignDhcp: trunked ? false : nic.assignDhcp || false,
    ipAddress: trunked ? '' : nic.ipAddress || '',
    macAddress: nic.macAddress || '',
    networkName: nic.network?.uid || '',
    trunked,
    trunkedNetworks: getTrunkedNetworkUidsFromNic(nic),
    type: nic.type || '',
  };
}

export function trunkedNetworksEqual(
  currentTrunkedNetworkUids: string[],
  originalTrunkedNetworkUids: string[],
): boolean {
  if (currentTrunkedNetworkUids.length !== originalTrunkedNetworkUids.length) {
    return false;
  }

  const sortedCurrent = [...currentTrunkedNetworkUids].sort();
  const sortedOriginal = [...originalTrunkedNetworkUids].sort();

  return sortedCurrent.every(
    (networkUid, index) => networkUid === sortedOriginal[index],
  );
}

export function isEditNicFormUnchanged(
  values: EditNicFormOriginalValues,
  originalValues: EditNicFormOriginalValues,
): boolean {
  const sharedFieldsMatch =
    Boolean(values.trunked) === Boolean(originalValues.trunked) &&
    (values.type ?? '') === (originalValues.type ?? '') &&
    (values.macAddress ?? '') === (originalValues.macAddress ?? '');

  if (!sharedFieldsMatch) {
    return false;
  }

  if (originalValues.trunked) {
    return (
      Boolean(values.assignDhcp) === Boolean(originalValues.assignDhcp) &&
      (values.ipAddress ?? '') === (originalValues.ipAddress ?? '') &&
      trunkedNetworksEqual(
        values.trunkedNetworks ?? [],
        originalValues.trunkedNetworks ?? [],
      )
    );
  }

  return (
    (values.networkName ?? '') === (originalValues.networkName ?? '') &&
    Boolean(values.assignDhcp) === Boolean(originalValues.assignDhcp) &&
    (values.ipAddress ?? '') === (originalValues.ipAddress ?? '')
  );
}

export function applyTrunkedEnabledFormChange(
  value: boolean,
  stashedTrunkedNetworks: string[],
  nicToEdit: VmNetworkInterfaceToEdit,
  originalValues: EditNicFormOriginalValues,
  setValue: UseFormMethods<NetworkingFormData>['setValue'],
): void {
  if (!value) {
    return;
  }

  const restoredTrunkedNetworks = getRestoredTrunkedNetworks(
    stashedTrunkedNetworks,
    nicToEdit,
  );

  setValue('assignDhcp', false, trunkedEnabledFieldOptions);
  setValue('ipAddress', '', trunkedEnabledFieldOptions);
  setValue(
    'networkName',
    originalValues.networkName ?? '',
    trunkedEnabledFieldOptions,
  );
  setValue(
    'trunkedNetworks',
    restoredTrunkedNetworks,
    trunkedEnabledFieldOptions,
  );
}
